import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { omit, pick, set } from 'lodash';
import filterValueQuery from '../data/filterValueQuery.graphql';
import updateFilterValueMutation from '../data/updateFilterValueMutation.graphql';
const RESET_ON_LOGIN_VERSION_KEY = 'FILTER_RESET_VERSION';
const RESET_ON_LOGIN_APPLIED_PREFIX = 'RESET_ON_LOGIN_APPLIED_';
export default function useFilterStore(filterKey, { defaultValues = {}, initialChange, searchName = 'searchQuery', searchQuery, onBeforeUpdate, onBeforeComplete, resetOnLogin = false, resetOnLoginKeys = [], skip, fetchPolicy, } = {}) {
    const [values, setValues] = useState();
    const valuesRef = useRef(values);
    valuesRef.current = values;
    const defaultValuesRef = useRef(defaultValues);
    defaultValuesRef.current = defaultValues;
    const lastProcessedRawDataRef = useRef();
    const selfWrittenValuesRef = useRef(new Set());
    const initialChangeRef = useRef(initialChange);
    initialChangeRef.current = initialChange;
    const onBeforeCompleteRef = useRef(onBeforeComplete);
    onBeforeCompleteRef.current = onBeforeComplete;
    const onBeforeUpdateRef = useRef(onBeforeUpdate);
    onBeforeUpdateRef.current = onBeforeUpdate;
    const [updateFilterValue, { error: errorUpdate, loading: loadingUpdate },] = useMutation(updateFilterValueMutation);
    const persistFilterValues = useCallback((_values) => {
        if (!filterKey) {
            return;
        }
        const clearedValues = omit(_values, [searchName]);
        const preparedValue = onBeforeUpdateRef.current
            ? onBeforeUpdateRef.current(clearedValues)
            : clearedValues;
        const serializedValue = JSON.stringify(preparedValue);
        lastProcessedRawDataRef.current = serializedValue;
        selfWrittenValuesRef.current.add(serializedValue);
        updateFilterValue({
            variables: {
                filterKey,
                values: serializedValue,
            },
            update: cache => {
                try {
                    cache.writeQuery({
                        query: filterValueQuery,
                        variables: { filterKey },
                        data: { filterValue: serializedValue },
                    });
                }
                catch (e) {
                    // cache may not have query
                }
            },
        });
    }, [filterKey, searchName, updateFilterValue]);
    const processFilterData = useCallback((filterValue) => {
        if (filterValue === undefined) {
            return;
        }
        let parsedValues = filterValue
            ? JSON.parse(filterValue)
            : defaultValuesRef.current;
        if (resetOnLogin &&
            filterKey &&
            typeof window !== 'undefined' &&
            resetOnLoginKeys.length) {
            const resetVersion = localStorage.getItem(RESET_ON_LOGIN_VERSION_KEY);
            const appliedVersionKey = `${RESET_ON_LOGIN_APPLIED_PREFIX}${filterKey}`;
            const appliedVersion = localStorage.getItem(appliedVersionKey);
            const shouldResetValues = !!resetVersion && resetVersion !== appliedVersion;
            if (shouldResetValues && resetVersion) {
                parsedValues = {
                    ...omit(parsedValues, resetOnLoginKeys),
                    ...pick(defaultValuesRef.current, resetOnLoginKeys),
                };
                localStorage.setItem(appliedVersionKey, resetVersion);
                persistFilterValues(parsedValues);
            }
        }
        const _values = onBeforeCompleteRef.current
            ? onBeforeCompleteRef.current(parsedValues)
            : parsedValues;
        set(_values, [searchName], searchQuery);
        setValues(_values);
        if (initialChangeRef.current) {
            initialChangeRef.current(_values);
        }
    }, [
        filterKey,
        persistFilterValues,
        resetOnLogin,
        resetOnLoginKeys,
        searchName,
        searchQuery,
    ]);
    const { data, loading: loadingQuery, error: errorQuery } = useQuery(filterValueQuery, {
        variables: { filterKey },
        skip: !filterKey || skip,
        fetchPolicy: fetchPolicy || 'cache-first',
    });
    useEffect(() => {
        if (data) {
            if (lastProcessedRawDataRef.current === data.filterValue ||
                (!!data.filterValue &&
                    selfWrittenValuesRef.current.has(data.filterValue))) {
                return;
            }
            lastProcessedRawDataRef.current = data.filterValue;
            processFilterData(data.filterValue);
        }
    }, [data, processFilterData]);
    const onChange = useCallback((values) => {
        if (filterKey) {
            setValues(values);
            persistFilterValues(values);
        }
    }, [persistFilterValues, filterKey]);
    return {
        values,
        onChange,
        error: filterKey ? errorUpdate || errorQuery : undefined,
        loading: !!filterKey && (loadingUpdate || loadingQuery),
    };
}
